import {
  closeEquationNumber as close, unwrapEquationNode as unwrap,
  equationParts, equationPolynomial as polynomial, addPolynomials as add, polynomialNumber as numberOf,
} from "./equationPolynomial";

function circleFromPolynomial(poly) {
  const coefficient = poly.get("2,0") || 0;
  if (coefficient === 0 || !close((poly.get("0,2") || 0) / coefficient, 1)
    || !close((poly.get("1,1") || 0) / coefficient, 0)) return null;
  const centerX = -(poly.get("1,0") || 0) / (2 * coefficient);
  const centerY = -(poly.get("0,1") || 0) / (2 * coefficient);
  const rSquared = centerX ** 2 + centerY ** 2 - (poly.get("0,0") || 0) / coefficient;
  return rSquared > 0 && Number.isFinite(rSquared) ? { centerX, centerY, rSquared } : null;
}

export function parseCircleEquation(input) {
  try {
    const [left, right] = equationParts(input);
    return circleFromPolynomial(add(polynomial(left), polynomial(right), -1));
  } catch { return null; }
}

// Tasks explicitly asking for canonical form must still require two squares.
export function parseCanonicalCircleEquation(input) {
  try {
    const [left, right] = equationParts(input);
    const sum = unwrap(left);
    if (!sum.isOperatorNode || sum.op !== "+" || sum.args.length !== 2) return null;
    const variables = sum.args.map(term => {
      const square = unwrap(term);
      if (!square.isOperatorNode || square.op !== "^" || numberOf(polynomial(square.args[1])) !== 2) return null;
      const base = polynomial(square.args[0]);
      if ([...base].some(([key, value]) => value !== 0 && !["0,0", "1,0", "0,1"].includes(key))) return null;
      if (close(base.get("1,0") || 0, 1) && (base.get("0,1") || 0) === 0) return "x";
      if (close(base.get("0,1") || 0, 1) && (base.get("1,0") || 0) === 0) return "y";
      return null;
    });
    if (!variables.includes("x") || !variables.includes("y")) return null;
    numberOf(polynomial(right));
    return circleFromPolynomial(add(polynomial(left), polynomial(right), -1));
  } catch { return null; }
}

export function checkCircleEquation(input, expected, { canonical = false } = {}) {
  const actual = canonical ? parseCanonicalCircleEquation(input) : parseCircleEquation(input);
  const target = typeof expected === "string" ? parseCircleEquation(expected) : expected;
  return Boolean(actual && target && ["centerX", "centerY", "rSquared"]
    .every(key => Number.isFinite(target[key]) && close(actual[key], target[key])));
}
